import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FloatingPortal } from '@floating-ui/react';
import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnimatePresence, motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { SMSSendV3 } from '@frontend/api-sms-send';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { MiniChatContext, useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { MiniChat } from './mini-chat';
import { MobileMiniChatSelectionMenuButton } from './mobile-selection-menu';

export const MobileMiniChatManager = () => {
  const alert = useAlert();
  const { t } = useTranslation('mini-chat');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();
  const aclEnforcementEnabled = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: 'nwx:messaging-inbox-fe-acl-enforcement',
    strategy: 'ALL',
  });
  const locationIdsWithACL = aclEnforcementEnabled
    ? filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      })
    : selectedLocationIds;
  const {
    chats,
    openChat,
    minimizeChat,
    removeChat,
    removeChats,
    activeChatId,
    addChat: changeThread,
    errorBoundaryActive,
  } = useMiniChatShallowStore(
    'chats',
    'rolledUp',
    'openChat',
    'minimizeChat',
    'removeChat',
    'removeChats',
    'activeChatId',
    'addChat',
    'errorBoundaryActive'
  );
  const { navigateToThread } = useInboxNavigate();
  const [chatSelectionMenuIsOpen, setChatSelectionMenuIsOpen] = useState(false);
  const currentOpenChat = useMemo<MiniChatContext | undefined>(
    () => chats.find(({ chatId }) => chatId === activeChatId),
    [activeChatId, JSON.stringify(chats)]
  );
  const { possiblePersonPhones, ...chatProps } = currentOpenChat ?? {};

  const { data: locationPhonesData } = InboxLocationPhones.Hooks.useLocationPhones();
  const currentChatLocationPhones = locationPhonesData.locationPhonesByLocation[currentOpenChat?.groupId ?? ''] ?? [];
  const isCurrentChatGroup =
    currentOpenChat?.threadType === ThreadType.THREAD_TYPE_GROUP_MMS &&
    (currentOpenChat?.groupThreadContext?.participants?.length ?? 0) > 1;
  const currentChatCanChangeThreads =
    (currentOpenChat?.possiblePersonPhones.length ?? 0) > 1 ||
    currentChatLocationPhones.length > 1 ||
    locationPhonesData.allLocationPhones.length > 1 ||
    isCurrentChatGroup;

  const locationPhonesByLocation = useMemo<Record<string, string[]>>(() => {
    return Object.fromEntries(
      Object.entries(locationPhonesData.locationPhonesByLocation).map(([locationId, locationPhones]) => [
        locationId,
        locationPhones.map(({ locationPhone }) => locationPhone),
      ])
    );
  }, [locationPhonesData.locationPhonesByLocation]);

  // Single thread status query — enabled only for non-group chats
  const threadStatusQueryV4 = SMSDataV4.Queries.useBatchGetThreadStatusQuery(
    {
      requests: SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
        personPhones: currentOpenChat?.possiblePersonPhones ?? [],
        locationPhonesByLocation,
      }),
    },
    {
      enabled: currentChatCanChangeThreads && !isCurrentChatGroup,
    }
  );

  const { mutateAsync: groupCheckAsync } = SMSSendV3.Mutations.useGroupCheckMutation();

  const openChatSelectionMenu = useCallback(() => {
    minimizeChat();
    setChatSelectionMenuIsOpen(true);
  }, [minimizeChat, setChatSelectionMenuIsOpen]);

  const selectChat = useCallback(
    (chatId: string) => {
      openChat(chatId);
      setChatSelectionMenuIsOpen(false);
    },
    [openChat, setChatSelectionMenuIsOpen]
  );

  const closeChatSelectionMenu = useCallback(() => {
    setChatSelectionMenuIsOpen(false);
  }, [setChatSelectionMenuIsOpen]);

  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const invalidChats = chats.filter((chat) => !locationIdsWithACL.includes(chat.groupId));
    if (invalidChats.length) removeChats(invalidChats.map((chat) => chat.chatId));
  }, [JSON.stringify(locationIdsWithACL)]);

  return (
    <FloatingPortal>
      <motion.span
        css={{
          position: 'absolute',
          right: 0,
          left: 0,
          padding: theme.spacing(0, 8, 0, 1),
          display: 'flex',
          justifyContent: 'flex-end',
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 10000,
          '*': {
            pointerEvents: 'auto',
          },
          overflowX: 'clip',
        }}
        ref={ref}
      >
        <AnimatePresence>
          {!chatSelectionMenuIsOpen && !!currentOpenChat && !errorBoundaryActive && (
            <MiniChat
              {...(chatProps as Omit<MiniChatContext, 'possiblePersonPhones'>)}
              sizeVariant='full'
              isMobile
              onMaximize={() => {
                navigateToThread({
                  threadId: currentOpenChat.threadId,
                  groupId: currentOpenChat.groupId,
                  personId: currentOpenChat.contextPersonId,
                  locationPhone: currentOpenChat.locationPhone,
                  personPhone: currentOpenChat.personPhone,
                  threadItemId: currentOpenChat.focusThreadItemData?.id,
                  threadItemCreatedAt: currentOpenChat.focusThreadItemData?.createdAt,
                });
                removeChat(currentOpenChat.chatId);
              }}
              onClose={() => removeChat(currentOpenChat.chatId)}
              onSelectPersonPhone={(personPhone) => {
                const newThread = threadStatusQueryV4.data?.threadStatuses.find(
                  (response) =>
                    response.personPhone === formatPhoneNumberE164(personPhone) &&
                    response.locationId === currentOpenChat.groupId &&
                    (currentOpenChat.locationPhone ? response.locationPhone === currentOpenChat.locationPhone : true)
                );
                if (!newThread) {
                  alert.error(t('Error switching to conversation.'));
                  sentry.error({
                    error: 'Error finding selected thread from thread status query response',
                    topic: 'messages',
                    team: 'fe-messaging',
                    addContext: {
                      name: 'Thread selection data',
                      context: {
                        threadStatusQuery: threadStatusQueryV4,
                        currentOpenChat,
                        newPersonPhone: personPhone,
                      },
                    },
                  });
                  return;
                }

                changeThread({
                  personPhone,
                  groupId: currentOpenChat.groupId,
                  contextPersonId: currentOpenChat.contextPersonId,
                  locationPhone: currentOpenChat.locationPhone,
                  unreadCount: 0,
                  threadId: newThread.threadId,
                  isNew: newThread.status === ThreadStatus.NEW,
                });
              }}
              onSelectLocationPhone={({ locationPhone, locationId }, threadId) => {
                if (!currentOpenChat) return;

                // For group threads, validate via GroupCheck and resolve thread
                if (isCurrentChatGroup && currentOpenChat.groupThreadContext?.participants?.length) {
                  const resolvedLocationId = locationId || currentOpenChat.groupId;
                  groupCheckAsync({
                    locationId: resolvedLocationId,
                    locationPhone: locationPhone || '',
                    participants: currentOpenChat.groupThreadContext.participants.map((p) => ({
                      phoneNumber: p.phoneNumber || '',
                      person: p.person,
                    })),
                  })
                    .then((result) => {
                      if (!result.threadId) {
                        alert.error(t('Unable to switch to group conversation with the selected number.'));
                        return;
                      }
                      changeThread({
                        personPhone: currentOpenChat.personPhone,
                        groupId: resolvedLocationId,
                        contextPersonId: currentOpenChat.contextPersonId,
                        locationPhone: locationPhone || currentOpenChat.locationPhone,
                        unreadCount: 0,
                        threadId: result.threadId,
                        threadType: ThreadType.THREAD_TYPE_GROUP_MMS,
                        groupThreadContext: currentOpenChat.groupThreadContext,
                        isNew: result.groupCheckStatus === 'GROUP_CHECK_STATUS_AVAILABLE',
                      });
                    })
                    .catch(() => {
                      alert.error(t('Error switching to conversation.'));
                    });
                  return;
                }

                // For single threads, use the single thread status query
                const newThread = threadStatusQueryV4.data?.threadStatuses.find((response) =>
                  threadId
                    ? response.threadId === threadId
                    : response.personPhone === formatPhoneNumberE164(currentOpenChat.personPhone) &&
                      formatPhoneNumberE164(response.locationPhone) === formatPhoneNumberE164(locationPhone ?? '') &&
                      response.locationId === locationId
                );

                if (!newThread) {
                  alert.error(t('Error switching to conversation.'));
                  sentry.error({
                    error: 'Error finding selected thread from thread status query response',
                    topic: 'messages',
                    team: 'fe-messaging',
                    addContext: {
                      name: 'Thread selection data',
                      context: {
                        threadStatusQuery: threadStatusQueryV4,
                        currentOpenChat,
                        newLocationPhone: locationPhone,
                        locationPhone,
                      },
                    },
                  });
                  return;
                }
                changeThread({
                  personPhone: currentOpenChat.personPhone,
                  groupId: newThread.locationId,
                  contextPersonId: currentOpenChat.contextPersonId,
                  locationPhone: locationPhone || newThread.locationPhone,
                  unreadCount: 0,
                  threadId: newThread.threadId,
                  isNew: newThread.status === ThreadStatus.NEW,
                });
              }}
              onMinimize={() => minimizeChat()}
            />
          )}
        </AnimatePresence>
        <MobileMiniChatSelectionMenuButton
          chatSelectionMenuIsOpen={chatSelectionMenuIsOpen}
          onClick={() => {
            if (chatSelectionMenuIsOpen) {
              closeChatSelectionMenu();
            } else {
              openChatSelectionMenu();
            }
          }}
          selectChat={selectChat}
        />
      </motion.span>
    </FloatingPortal>
  );
};
